import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db, WorkoutSession, Routine, TrainingDay } from '../database';

const Progress: React.FC = () => {
  const [completedSessions, setCompletedSessions] = useState<WorkoutSession[]>([]);
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [trainingDays, setTrainingDays] = useState<TrainingDay[]>([]);

  useEffect(() => {
    loadProgressData();
  }, []);

  const loadProgressData = async () => {
    try {
      // No loading state needed for local data
      const [sessions, routinesData, trainingDaysData] = await Promise.all([
        db.workoutSessions.filter(session => session.completed).reverse().toArray(),
        db.routines.toArray(),
        db.trainingDays.toArray()
      ]);
      
      setCompletedSessions(sessions);
      setRoutines(routinesData);
      setTrainingDays(trainingDaysData);
    } catch (error) {
      console.error('Failed to load progress data:', error);
    }
  };

  const getRoutineName = (routineId: number) => {
    return routines.find(r => r.id === routineId)?.name || 'Unknown Routine';
  };

  const getTrainingDayName = (trainingDayId: number) => {
    return trainingDays.find(td => td.id === trainingDayId)?.name || 'Unknown Workout';
  };

  const formatDuration = (start: Date, end?: Date) => {
    if (!end) return 'In progress...';
    const durationMs = end.getTime() - start.getTime();
    const minutes = Math.floor(durationMs / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  const getStreakInfo = () => {
    if (completedSessions.length === 0) return { current: 0, total: completedSessions.length };
    
    let currentStreak = 0;
    const today = new Date();
    const sessions = [...completedSessions].reverse(); // Most recent first
    
    for (let i = 0; i < sessions.length; i++) {
      const sessionDate = new Date(sessions[i].startTime);
      const daysDiff = Math.floor((today.getTime() - sessionDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff <= 7) { // Within a week
        currentStreak++;
      } else {
        break;
      }
    }
    
    return { current: currentStreak, total: completedSessions.length };
  };

  // No loading check needed for local data

  const streak = getStreakInfo();

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-900">Progress & History</h2>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-900">{streak.total}</div>
          <div className="text-blue-700 text-sm">Total Workouts</div>
        </div>
        <div className="bg-green-50 border border-green-200 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-900">{streak.current}</div>
          <div className="text-green-700 text-sm">Recent Streak</div>
        </div>
      </div>

      {/* Workout History */}
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-900">Workout History</h3>
        
        {completedSessions.length === 0 ? (
          <div className="text-center text-gray-500 py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-4xl mb-2">📊</div>
            <p className="mb-2">No completed workouts yet</p>
            <p className="text-sm text-gray-400">Start your first workout to see progress here</p>
            <Link to="/" className="inline-block mt-3 text-blue-600 text-sm">
              Go to Home →
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {completedSessions.slice(0, 10).map((session) => (
              <Link
                key={session.id}
                to={`/summary/${session.id}`}
                className="block bg-white border rounded-lg p-4 hover:border-blue-300"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">
                      {getTrainingDayName(session.trainingDayId)}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {getRoutineName(session.routineId)}
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      {new Date(session.startTime).toLocaleDateString()} • 
                      {formatDuration(new Date(session.startTime), session.endTime ? new Date(session.endTime) : undefined)}
                    </p>
                  </div>
                  <div className="text-gray-400">
                    ›
                  </div>
                </div>
              </Link>
            ))}
            
            {completedSessions.length > 10 && (
              <div className="text-center pt-2">
                <button className="text-blue-600 text-sm">
                  View all {completedSessions.length} workouts
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Settings */}
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-900">Settings</h3>
        
        <div className="bg-white border rounded-lg p-4">
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Database Management</h4>
              <p className="text-sm text-gray-600 mb-3">
                Reset all data including workouts, routines, and exercises. This cannot be undone.
              </p>
              <button
                onClick={async () => {
                  if (window.confirm('Are you sure you want to delete ALL data? This cannot be undone!')) {
                    try {
                      await db.delete();
                      await db.open();
                      window.location.reload();
                    } catch (error) {
                      console.error('Failed to purge database:', error);
                      alert('Failed to purge database. Check console for details.');
                    }
                  }
                }}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 cursor-pointer"
                style={{ pointerEvents: 'auto' }}
              >
                Purge All Data
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Progress;